import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useToastStore } from "@/components";
import { careplanQueryOptions } from "@/domains/careplan/factories/careplan-query-options";
import cartLinesAddMutation from "@/domains/careplan/models/graphql/cart-lines-add-mutation.graphql?raw";
import {
  CartLineNetworkError,
  CartLineOutOfStockError,
  CartLineValidationError,
  getCartLineErrorMessage,
  STOCK_WARNING_CODES,
} from "@/domains/careplan/mutations/cart-line-errors";
import { storefrontApi } from "@/modules/network";
import type {
  CartLinesAddMutation,
  CartLinesAddMutationVariables,
} from "@/types/storefront-api.gen";

export const useAddCareplanCartLine = () => {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((state) => {
    return state.pushToast;
  });

  return useMutation({
    mutationFn: addCareplanCartLine,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: careplanQueryOptions.cart.queryKey,
      });
    },
    onError: async (error) => {
      await pushToast({
        type: "info",
        title: "Add to Cart Failed",
        message: getCartLineErrorMessage(error),
      });
    },
  });
};

export const addCareplanCartLine = async (variables: CartLinesAddMutationVariables) => {
  let response: Awaited<ReturnType<typeof storefrontApi.request<CartLinesAddMutation>>>;

  try {
    response = await storefrontApi.request<CartLinesAddMutation>(cartLinesAddMutation, {
      variables,
    });
  } catch (error) {
    throw new CartLineNetworkError("Failed to reach Shopify while adding to cart", {
      cause: error,
    });
  }

  const requestedLine = Array.isArray(variables.lines) ? variables.lines[0] : variables.lines;

  const cartLines = response.data?.cartLinesAdd?.cart?.lines.edges ?? [];
  const updatedLine = cartLines.find(({ node }) => {
    return node.merchandise.id === requestedLine.merchandiseId;
  })?.node;

  const stockWarning = response.data?.cartLinesAdd?.warnings.find((warning) => {
    return STOCK_WARNING_CODES.has(warning.code) && warning.target === updatedLine?.id;
  });

  if (stockWarning) {
    throw new CartLineOutOfStockError(stockWarning.message);
  }

  if (updatedLine?.quantity !== requestedLine.quantity) {
    const shopifyMessage = response.data?.cartLinesAdd?.userErrors[0]?.message;

    if (shopifyMessage) {
      throw new CartLineValidationError(shopifyMessage);
    }

    throw new CartLineOutOfStockError(
      "Could not add item to cart — the requested quantity was not applied",
    );
  }

  return response.data;
};
